/**
 * useOfflineReviews — single source of truth for the offline-review
 * status indicator (✓ Synced / ⚡ N pending / 📡 Offline) and the manual
 * "Sync now" affordance.
 *
 * Listens to:
 *   - window 'online' / 'offline' events
 *   - storage events from other tabs (so a sync on tab A updates tab B)
 *   - a 30s polling interval as a final safety net while the page is
 *     mounted (covers the case where the browser doesn't fire 'online'
 *     for sketchy captive-portal-style reconnects).
 *
 * Returns:
 *   isOnline:      boolean — current navigator.onLine
 *   pendingCount:  number  — items in the localStorage queue
 *   lastSyncAt:    string|null — ISO timestamp of the last successful sync
 *   syncStatus:    'idle' | 'syncing' | 'synced' | 'error'
 *   syncNow:       async () => result of syncPendingReviews
 *
 * The hook auto-syncs whenever the queue is non-empty AND we transition
 * to online (window 'online' event), so the user usually doesn't need to
 * tap the manual button.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getPendingReviews,
  getLastSyncAt,
  syncPendingReviews,
} from '../utils/offlineReviews';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useOfflineReviews = () => {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [pendingCount, setPendingCount] = useState(
    () => getPendingReviews().length
  );
  const [lastSyncAt, setLastSyncAt] = useState(() => getLastSyncAt());
  const [syncStatus, setSyncStatus] = useState('idle');
  // Prevent concurrent sync attempts (e.g. 'online' event + manual click).
  const syncInFlightRef = useRef(false);

  const refreshFromStorage = useCallback(() => {
    setPendingCount(getPendingReviews().length);
    setLastSyncAt(getLastSyncAt());
  }, []);

  const syncNow = useCallback(async () => {
    if (syncInFlightRef.current) return null;
    if (!navigator.onLine) {
      setSyncStatus('error');
      return null;
    }
    if (getPendingReviews().length === 0) {
      setSyncStatus('synced');
      return { synced: 0, skipped: 0, failed: 0, dropped: 0, attempted: 0 };
    }
    syncInFlightRef.current = true;
    setSyncStatus('syncing');
    try {
      const result = await syncPendingReviews(API);
      refreshFromStorage();
      setSyncStatus('synced');
      return result;
    } catch (err) {
      // Queue is preserved — next 'online' tick will retry.
      setSyncStatus('error');
      return null;
    } finally {
      syncInFlightRef.current = false;
    }
  }, [refreshFromStorage]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Fire-and-forget — best effort.
      if (getPendingReviews().length > 0) {
        syncNow();
      }
    };
    const handleOffline = () => {
      setIsOnline(false);
      setSyncStatus('idle');
    };
    const handleStorage = (e) => {
      if (
        e.key === 'istudypro_pending_reviews_v1' ||
        e.key === 'istudypro_last_sync_v1'
      ) {
        refreshFromStorage();
      }
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('storage', handleStorage);

    // Polling safety net for flaky-reconnect scenarios. Skip when the
    // tab is hidden so background tabs don't spin the CPU.
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      refreshFromStorage();
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, [refreshFromStorage, syncNow]);

  return {
    isOnline,
    pendingCount,
    lastSyncAt,
    syncStatus,
    syncNow,
    refreshFromStorage,
  };
};

export default useOfflineReviews;
